<template>
  <v-container fluid class="pf">
    <page-skeleton v-if="loading" variant="profile" class="mt-4" />

    <template v-else>
      <v-form v-model="valid" ref="form">
        <!-- ============ CARTEIRA (identidade) ============ -->
        <div class="pf-card-id">
          <input
            type="file"
            ref="fileInput"
            id="fileInput"
            accept="image/*"
            class="pf-file"
            @change="onFilePick"
          />

          <button
            type="button"
            class="pf-avatar"
            @click="triggerFileInput"
            :aria-label="$t('profile.changePhoto')"
          >
            <v-img
              v-if="hasAvatar"
              cover
              :src="profile.avatar"
              class="pf-avatar__img"
            />
            <span v-else class="pf-avatar__initials">{{ initials }}</span>
            <span class="pf-avatar__cam">
              <v-icon size="18" color="#fff">mdi-camera-outline</v-icon>
            </span>
          </button>

          <div class="pf-id__body">
            <div class="pf-eyebrow">{{ $t("partnerProfile.eyebrow") }}</div>
            <h1 class="pf-display pf-id__name">
              {{ profile.fullName || $t("profile.yourProfile") }}
            </h1>

            <div class="pf-id__tiles">
              <div class="pf-tile">
                <span class="pf-display pf-tile__num">{{
                  countLabel(profile.studentsCount)
                }}</span>
                <span class="pf-tile__label">{{
                  $tc(
                    "partnerProfile.students",
                    Number(profile.studentsCount) === 1 ? 1 : 2
                  )
                }}</span>
              </div>
              <div class="pf-tile">
                <span class="pf-display pf-tile__num">{{
                  countLabel(profile.classesCount)
                }}</span>
                <span class="pf-tile__label">{{
                  $tc(
                    "partnerProfile.classes",
                    Number(profile.classesCount) === 1 ? 1 : 2
                  )
                }}</span>
              </div>
              <span v-if="profile.tutor" class="pf-chip pf-chip--tutor">
                <v-icon size="14" class="mr-1">mdi-account-star-outline</v-icon>
                {{ $t("partnerProfile.tutor") }}
              </span>
            </div>

            <div
              v-if="profile.certificates && profile.certificates.length"
              class="pf-id__quals"
            >
              <span
                v-for="(item, index) in profile.certificates"
                :key="index"
                class="pf-chip"
              >
                {{ displayCertificateName(item) }}
              </span>
            </div>
          </div>

          <div class="pf-id__actions">
            <v-btn rounded outlined class="pf-btn-ghost" @click="returnToHome">
              <v-icon left small>mdi-arrow-left</v-icon>{{ $t("profile.back") }}
            </v-btn>
            <v-btn
              rounded
              depressed
              class="pf-btn"
              :loading="loadingSave"
              @click="submit"
            >
              <v-icon left small>mdi-content-save-outline</v-icon
              >{{ $t("profile.update") }}
            </v-btn>
          </div>
        </div>

        <!-- ============ DISPONIBILIDADE PARA NOVOS ALUNOS ============ -->
        <div class="pf-avail">
          <v-switch
            :input-value="profile.acceptingNewStudents !== false"
            :loading="savingAvailability"
            :disabled="savingAvailability"
            inset
            hide-details
            color="#00a379"
            class="pf-avail__switch"
            :aria-label="$t('partnerProfile.acceptingAria')"
            @change="toggleAcceptingStudents"
          />
          <div class="pf-avail__text">
            <div class="pf-avail__title">
              {{
                profile.acceptingNewStudents !== false
                  ? $t("partnerProfile.accepting")
                  : $t("partnerProfile.notAccepting")
              }}
            </div>
            <div class="pf-avail__sub">
              {{ $t("partnerProfile.acceptingSub") }}
            </div>
          </div>
        </div>

        <!-- ============ RAIL DE ABAS ============ -->
        <div class="pf-rail" role="tablist">
          <button
            v-for="t in visibleTabs"
            :key="t.value"
            type="button"
            role="tab"
            class="pf-rail__tab"
            :class="{
              'pf-rail__tab--on': tab === t.value,
              'pf-rail__tab--alert': t.alert,
            }"
            :aria-selected="tab === t.value"
            @click="tab = t.value"
          >
            <v-icon size="18" class="mr-1">{{ t.icon }}</v-icon>
            {{ t.label }}
          </button>
        </div>

        <v-tabs-items v-model="tab" class="pf-tabs-items">
          <!-- ---------- Dados gerais ---------- -->
          <v-tab-item value="tab-1">
            <div class="pf-card pf-card--pad">
              <div class="pf-section-title">{{ $t("profile.personal") }}</div>
              <v-row dense class="mt-2">
                <v-col cols="12" md="4">
                  <custom-input
                    :outlined="true"
                    v-model="profile.fullName"
                    :label="$t('profile.name')"
                    :currentValue="profile.fullName"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <custom-masked-input
                    ref="phone"
                    v-model="profile.phone"
                    :outlined="true"
                    :label="$t('profile.phone')"
                    :currentValue="profile.phone"
                    :mask="'(##) #####-####'"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <custom-input
                    :outlined="true"
                    :label="$t('profile.email')"
                    v-model="profile.email"
                    :currentValue="profile.email"
                    :customRule="rules.email"
                  />
                </v-col>
              </v-row>
              <v-row dense>
                <v-col cols="12" md="4">
                  <custom-masked-input
                    ref="document"
                    :outlined="true"
                    :label="$t('profile.cpf')"
                    :currentValue="profile.document"
                    :customRule="rules.document"
                    v-model="profile.document"
                    :mask="'###.###.###-##'"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <custom-masked-input
                    :outlined="true"
                    :label="$t('profile.birthDate')"
                    v-model="profile.birthDate"
                    :currentValue="profile.birthDate"
                    :mask="'##/##/####'"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <custom-masked-input
                    :label="$t('partnerProfile.cnpj')"
                    v-model="profile.cnpj"
                    :outlined="true"
                    :uppercase="true"
                    :currentValue="profile.cnpj"
                    :mask="'NN.NNN.NNN/NNNN-##'"
                    :customRule="rules.validCNPJ"
                  />
                </v-col>
              </v-row>

              <div class="pf-subhead">
                <span class="pf-eyebrow">{{
                  $t("partnerProfile.aboutYou")
                }}</span>
              </div>
              <v-row dense>
                <v-col cols="12">
                  <v-textarea
                    v-model="profile.bio"
                    outlined
                    auto-grow
                    rows="3"
                    counter="600"
                    :rules="[
                      (v) =>
                        !v || v.length <= 600 || $t('partnerProfile.max600'),
                    ]"
                    :label="$t('partnerProfile.bioLabel')"
                    :hint="$t('partnerProfile.bioHint')"
                    persistent-hint
                  />
                </v-col>
              </v-row>

              <template v-if="profile.underAge">
                <div class="pf-subhead">
                  <span class="pf-eyebrow pf-eyebrow--ember">{{
                    $t("profile.guardian")
                  }}</span>
                </div>
                <div class="pf-inset">
                  <v-row dense>
                    <v-col cols="12" md="4">
                      <custom-input
                        v-model="profile.legalGuardian.name"
                        :currentValue="profile.legalGuardian.name"
                        :outlined="true"
                        :label="$t('profile.fullName')"
                      />
                    </v-col>
                    <v-col cols="12" md="4">
                      <custom-input
                        :outlined="true"
                        :label="$t('profile.email')"
                        :customRule="rules.email"
                        :currentValue="profile.legalGuardian.email"
                        v-model="profile.legalGuardian.email"
                      />
                    </v-col>
                    <v-col cols="12" md="4">
                      <custom-masked-input
                        :label="$t('profile.guardianPhone')"
                        placeholder="(11)99999-9999"
                        :hint="$t('profile.phoneHint')"
                        :mask="'(##)#####-####'"
                        :outlined="true"
                        v-model="profile.legalGuardian.phone"
                        :currentValue="profile.legalGuardian.phone"
                      />
                    </v-col>
                  </v-row>
                  <v-row dense>
                    <v-col cols="12" md="4">
                      <custom-masked-input
                        :outlined="true"
                        :label="$t('profile.cpf')"
                        :mask="'###.###.###-##'"
                        :customRule="rules.document"
                        :currentValue="profile.legalGuardian.document"
                        v-model="profile.legalGuardian.document"
                      />
                    </v-col>
                    <v-col cols="12" md="4">
                      <custom-masked-input
                        :outlined="true"
                        :label="$t('profile.birthDate')"
                        :mask="'##/##/####'"
                        :customRule="rules.validDate"
                        :currentValue="profile.legalGuardian.birthDate"
                        v-model="profile.legalGuardian.birthDate"
                      />
                    </v-col>
                    <v-col cols="12" md="4">
                      <div class="pf-field-label">
                        {{ $t("profile.kinship") }}
                      </div>
                      <v-select
                        outlined
                        rounded
                        background-color="var(--dk-wash, #F1F3F5)"
                        :items="parentGrades"
                        :item-value="`value`"
                        :item-text="`text`"
                        v-model="profile.legalGuardian.kinship"
                      />
                    </v-col>
                  </v-row>
                </div>
              </template>
            </div>

            <div class="pf-card pf-card--pad mt-4">
              <div class="pf-section-title">{{ $t("profile.address") }}</div>
              <v-row dense class="mt-2">
                <v-col cols="12" md="4">
                  <debounced-input
                    :outlined="true"
                    ref="zipCodeInput"
                    :label="$t('profile.zip')"
                    placeholder="12345678"
                    :hint="$t('profile.onlyNumbers')"
                    v-model="profile.address.zipCode"
                    :currentValue="profile.address.zipCode"
                    @search="getAddressByZipCode"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <custom-input
                    ref="streetInput"
                    :outlined="true"
                    :label="$t('profile.street')"
                    :currentValue="profile.address.street"
                    v-model="profile.address.street"
                    :placeholder="
                      loadingAddress
                        ? $t('profile.loading')
                        : $t('profile.streetPlaceholder')
                    "
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <custom-input
                    :outlined="true"
                    :label="$t('profile.number')"
                    :currentValue="profile.address.number"
                    v-model="profile.address.number"
                  />
                </v-col>
              </v-row>
              <v-row dense>
                <v-col cols="12" md="4">
                  <custom-input
                    :outlined="true"
                    :label="$t('profile.complement')"
                    :currentValue="profile.address.complement"
                    v-model="profile.address.complement"
                    :required="false"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <custom-input
                    :outlined="true"
                    ref="neighboorhoodInput"
                    :label="$t('profile.neighborhood')"
                    :currentValue="profile.address.neighboorhood"
                    v-model="profile.address.neighboorhood"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <custom-input
                    :outlined="true"
                    :label="$t('profile.city')"
                    ref="cityInput"
                    :currentValue="profile.address.city"
                    v-model="profile.address.city"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <custom-input
                    :outlined="true"
                    :label="$t('profile.state')"
                    ref="provinceInput"
                    :currentValue="profile.address.province"
                    v-model="profile.address.province"
                  />
                </v-col>
              </v-row>
            </div>
          </v-tab-item>

          <!-- ---------- Disponibilidade ---------- -->
          <v-tab-item value="tab-2">
            <div class="pf-card pf-card--pad">
              <div class="pf-section-title">{{ $t("profile.yourTimes") }}</div>
              <div class="pf-section-sub">
                {{ $t("partnerProfile.timesSub") }}
              </div>

              <div
                v-for="(available, index) in profile.aviability"
                :key="index"
                class="pf-slot"
              >
                <div class="pf-slot__field">
                  <div class="pf-field-label">{{ $t("profile.weekDay") }}</div>
                  <v-select
                    v-model="available.weekDay"
                    :items="weekDaysItems"
                    :item-text="`text`"
                    :item-value="`value`"
                    :disabled="!available.editable"
                    outlined
                    hide-details
                  />
                </div>
                <div class="pf-slot__field">
                  <div class="pf-field-label">{{ $t("profile.start") }}</div>
                  <v-select
                    v-model="available.from"
                    outlined
                    hide-details
                    :disabled="!available.editable"
                    :items="hoursItemsHalf"
                    @change="updateEnd(available)"
                  />
                </div>
                <div class="pf-slot__field">
                  <div class="pf-field-label">{{ $t("profile.end") }}</div>
                  <v-select
                    v-model="available.to"
                    outlined
                    hide-details
                    :disabled="!available.editable"
                    :items="hoursItemsHalf"
                  />
                </div>
                <v-btn
                  icon
                  class="pf-slot__del"
                  @click="removerDisponibilidade(available.id, index)"
                  :disabled="!available.editable"
                >
                  <v-icon>mdi-delete-outline</v-icon>
                </v-btn>
              </div>

              <button type="button" class="pf-add" @click="inserirHorario">
                <v-icon size="18" class="mr-1">mdi-plus</v-icon>
                {{ $t("profile.addTime") }}
              </button>
            </div>
          </v-tab-item>

          <!-- ---------- Plano de ensino ---------- -->
          <v-tab-item value="tab-3">
            <div class="pf-card pf-card--pad">
              <div class="pf-plans-head">
                <div>
                  <div class="pf-section-title">
                    {{ $t("partnerProfile.plansTitle") }}
                  </div>
                  <div class="pf-section-sub">
                    {{ $t("partnerProfile.plansSub") }}
                  </div>
                  <div class="pf-section-sub pf-plans-hint">
                    {{ $t("partnerProfile.plansMatchHint") }}
                  </div>
                </div>
                <v-btn
                  rounded
                  depressed
                  class="pf-btn"
                  :loading="loadingSave"
                  @click="openPlanForm"
                >
                  <v-icon left small>mdi-plus</v-icon
                  >{{ $t("partnerProfile.add") }}
                </v-btn>
              </div>

              <div v-if="teachingPlans.length === 0" class="pf-empty">
                {{ $t("partnerProfile.noPlans") }}
              </div>

              <div v-else class="pf-table">
                <div class="pf-table__head">
                  <span>{{ $t("partnerProfile.colModality") }}</span>
                  <span>{{ $t("partnerProfile.colFrequency") }}</span>
                  <span>{{ $t("partnerProfile.colValue") }}</span>
                  <span>{{ $t("partnerProfile.colDiscount") }}</span>
                  <span>{{ $t("partnerProfile.colMatch") }}</span>
                  <span class="pf-table__acts-h">{{
                    $t("partnerProfile.colActions")
                  }}</span>
                </div>
                <div
                  v-for="(item, index) in teachingPlans"
                  :key="index"
                  class="pf-table__row"
                >
                  <span class="pf-strong">{{
                    displayFormat(item.format)
                  }}</span>
                  <span>{{ displayFrequency(item.frequency) }}</span>
                  <span class="pf-money pf-money--in">{{
                    formatCurrency(item.value)
                  }}</span>
                  <span class="pf-money pf-money--off">{{
                    formatCurrency(item.discount)
                  }}</span>
                  <span class="pf-match">
                    <v-switch
                      :input-value="item.showToNewStudents !== false"
                      :loading="planVisibilityBusyId === item.id"
                      :disabled="planVisibilityBusyId === item.id"
                      inset
                      dense
                      hide-details
                      color="#00a379"
                      class="pf-match__switch"
                      :aria-label="$t('partnerProfile.matchAria')"
                      @change="togglePlanVisibility(item, $event)"
                    />
                    <span class="pf-match__label">{{
                      item.showToNewStudents !== false
                        ? $t("partnerProfile.matchOn")
                        : $t("partnerProfile.matchOff")
                    }}</span>
                  </span>
                  <span class="pf-table__acts">
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          v-on="on"
                          v-bind="attrs"
                          @click="editPlan(item)"
                          ><v-icon>mdi-pencil-outline</v-icon></v-btn
                        >
                      </template>
                      <span>{{ $t("common.edit") }}</span>
                    </v-tooltip>
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          v-on="on"
                          v-bind="attrs"
                          class="pf-slot__del"
                          @click="confirmDeletePlan(item)"
                          ><v-icon>mdi-delete-outline</v-icon></v-btn
                        >
                      </template>
                      <span>{{ $t("common.remove") }}</span>
                    </v-tooltip>
                  </span>
                </div>
              </div>

              <PlansDialog
                ref="planForm"
                :platformFee="profile.platformFee || 0"
                @savePlan="savePlan"
              />
              <v-dialog v-model="deletePlanDialog" max-width="460">
                <v-card>
                  <v-card-title class="text-h6">
                    {{ $t("partnerProfile.removePlanTitle") }}
                  </v-card-title>
                  <v-card-text>
                    <div v-if="loadingLinkedStudents" class="pf-plan-warn">
                      {{ $t("partnerProfile.checkingLinked") }}
                    </div>
                    <template v-else-if="planLinkedStudents.length">
                      <p>
                        {{
                          $tc(
                            "partnerProfile.linkedDelete",
                            planLinkedStudents.length,
                            { count: planLinkedStudents.length }
                          )
                        }}
                      </p>
                      <ul class="pf-linked-list">
                        <li
                          v-for="student in planLinkedStudents"
                          :key="student.id"
                        >
                          {{ student.name }}
                        </li>
                      </ul>
                    </template>
                    <p v-else>
                      {{ $t("partnerProfile.confirmDelete") }}
                    </p>
                  </v-card-text>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                      text
                      :disabled="deletingPlan"
                      @click="deletePlanDialog = false"
                    >
                      {{
                        planLinkedStudents.length
                          ? $t("common.close")
                          : $t("common.cancel")
                      }}
                    </v-btn>
                    <v-btn
                      v-if="!planLinkedStudents.length"
                      color="error"
                      :loading="deletingPlan"
                      :disabled="loadingLinkedStudents"
                      @click="deletePlan"
                    >
                      {{ $t("common.remove") }}
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>

              <v-dialog v-model="editPlanWarningDialog" max-width="460">
                <v-card>
                  <v-card-title class="text-h6">
                    {{ $t("partnerProfile.editLinkedTitle") }}
                  </v-card-title>
                  <v-card-text>
                    <p>
                      {{
                        $tc(
                          "partnerProfile.linkedEdit",
                          planLinkedStudents.length,
                          { count: planLinkedStudents.length }
                        )
                      }}
                    </p>
                    <ul class="pf-linked-list">
                      <li
                        v-for="student in planLinkedStudents"
                        :key="student.id"
                      >
                        {{ student.name }}
                      </li>
                    </ul>
                    <p class="pf-plan-warn">
                      {{ $t("partnerProfile.specificStudent") }}
                    </p>
                  </v-card-text>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="editPlanWarningDialog = false">
                      {{ $t("common.cancel") }}
                    </v-btn>
                    <v-btn color="primary" @click="proceedEditPlan">
                      {{ $t("common.continue") }}
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </div>
          </v-tab-item>

          <!-- ---------- Experiência profissional ---------- -->
          <v-tab-item v-if="profile.pendingInfo" value="tab-4">
            <div class="pf-card pf-card--pad">
              <div class="pf-section-title">
                {{ $t("partnerProfile.experienceTitle") }}
              </div>
              <div class="pf-section-sub">
                {{ $t("partnerProfile.experienceSub") }}
              </div>

              <div class="pf-q">
                <strong class="pf-q__label">{{
                  $t("partnerProfile.yearsQ")
                }}</strong>
                <v-radio-group
                  :rules="rules.required"
                  v-model="professionalInfo.yearsOfExperience"
                  class="mt-1"
                >
                  <v-radio
                    v-for="(experience, index) in experiencesLevelsOptions"
                    :key="index"
                    :label="experience.text"
                    :value="experience.value"
                  ></v-radio>
                </v-radio-group>
              </div>

              <div class="pf-q">
                <strong class="pf-q__label">{{
                  $t("partnerProfile.areaQ")
                }}</strong>
                <v-row dense>
                  <v-col
                    cols="12"
                    md="4"
                    v-for="(options, index) in techingPreferenceOptions"
                    :key="index"
                  >
                    <v-checkbox
                      hide-details
                      v-model="professionalInfo.expeciality"
                      :value="options.value"
                      :label="options.text"
                    ></v-checkbox>
                  </v-col>
                </v-row>
              </div>

              <div class="pf-q">
                <strong class="pf-q__label">{{
                  $t("partnerProfile.institutionsQ")
                }}</strong>
                <v-row dense>
                  <v-col
                    cols="12"
                    md="4"
                    v-for="(institution, index) in schoolsTypesOptions"
                    :key="index"
                  >
                    <v-checkbox
                      hide-details
                      v-model="professionalInfo.institutions"
                      :label="institution.text"
                      :value="institution.value"
                    ></v-checkbox>
                  </v-col>
                </v-row>
              </div>

              <div class="pf-q">
                <strong class="pf-q__label">{{
                  $t("partnerProfile.abroadQ")
                }}</strong>
                <v-row dense>
                  <v-col
                    cols="12"
                    md="4"
                    v-for="(options, index) in teachingExperiencieAbroadOptions"
                    :key="index"
                  >
                    <v-checkbox
                      hide-details
                      v-model="professionalInfo.teachingExperiencieAbroad"
                      :value="options.value"
                      :label="options.text"
                    ></v-checkbox>
                  </v-col>
                </v-row>
              </div>

              <div class="pf-q">
                <strong class="pf-q__label">{{
                  $t("partnerProfile.expertiseQ")
                }}</strong>
                <v-row dense>
                  <v-col
                    cols="12"
                    md="4"
                    v-for="(options, index) in expecialityOptions"
                    :key="index"
                  >
                    <v-checkbox
                      hide-details
                      :value="options.value"
                      :label="options.text"
                      v-model="professionalInfo.teachingPeference"
                    ></v-checkbox>
                  </v-col>
                </v-row>
              </div>

              <div class="pf-q">
                <strong class="pf-q__label">{{
                  $t("partnerProfile.certificatesQ")
                }}</strong>
                <v-row dense>
                  <v-col
                    cols="12"
                    md="4"
                    v-for="(options, index) in certificatesOptions"
                    :key="index"
                  >
                    <v-checkbox
                      hide-details
                      :value="options.value"
                      :label="options.text"
                      v-model="professionalInfo.certificateType"
                    ></v-checkbox>
                  </v-col>
                </v-row>
              </div>

              <div class="pf-q">
                <strong class="pf-q__label">{{
                  $t("partnerProfile.motivationQ")
                }}</strong>
                <v-row dense>
                  <v-col
                    cols="12"
                    md="4"
                    v-for="(options, index) in expectationOptions"
                    :key="index"
                  >
                    <v-checkbox
                      hide-details
                      :value="options.value"
                      v-model="professionalInfo.motivation"
                      :label="options.text"
                    ></v-checkbox>
                  </v-col>
                </v-row>
              </div>

              <div class="pf-q pf-q--last">
                <strong class="pf-q__label">{{
                  $t("partnerProfile.expectationsQ")
                }}</strong>
                <v-row dense>
                  <v-col
                    cols="12"
                    md="4"
                    v-for="(options, index) in personalExpectationOptions"
                    :key="index"
                  >
                    <v-checkbox
                      hide-details
                      :value="options.value"
                      :label="options.text"
                      v-model="professionalInfo.expectation"
                    ></v-checkbox>
                  </v-col>
                </v-row>
              </div>
            </div>
          </v-tab-item>

          <!-- ---------- Treinamentos (card 86akptec0) ---------- -->
          <v-tab-item v-if="hasTrainings" value="tab-5">
            <div class="pf-card pf-card--pad">
              <div class="pf-section-title">
                {{ $t("partnerProfile.evolutionTitle") }}
              </div>
              <div class="pf-section-sub">
                {{ $t("partnerProfile.evolutionSub") }}
              </div>
              <PartnerTrainingProgress
                class="mt-4"
                :progress="trainingProgress"
              />
            </div>
          </v-tab-item>

          <!-- ---------- Vincular aluno (card 86akng14v) ---------- -->
          <v-tab-item value="tab-invite">
            <div class="pf-card pf-card--pad">
              <StudentInvitePanel />
            </div>
          </v-tab-item>
        </v-tabs-items>
      </v-form>
    </template>
  </v-container>
</template>
<script>
import { cpf } from "cpf-cnpj-validator";
import { cleanCnpj, isValidCnpj } from "@/shared/cnpj";
import addressAPI from "@/shared/addressApi";
import partnerApi from "@/shared/pitchPartnerApi";
import { isBefore, isValid, parse, differenceInYears } from "date-fns";
import PlansDialog from "@/components/connect/partner/PartnerPlanForm.vue";
import PartnerTrainingProgress from "@/components/connect/partner/PartnerTrainingProgress.vue";
import StudentInvitePanel from "@/components/connect/partner/StudentInvitePanel.vue";
import {
  WEEK_DAY_ITEMS,
  displayGroupFormat,
  displayPlanFrequency,
} from "@/shared/classPresentation";
import { formatBRL } from "@/plugins/i18n";

// Opções das perguntas de experiência: o valor vai para a API como está; o
// rótulo vem de partnerProfile.opt.<grupo>.<valor>.
const OPTION_VALUES = {
  years: [
    "LESS_THAN_2_YEARS",
    "MORE_THAN_10_YEARS",
    "FROM_2_TO_5_YEARS",
    "UNEXPERIENCED",
    "FROM_6_TO_10_YEARS",
  ],
  schools: [
    "language_schools",
    "particular_classes",
    "online_courses",
    "regular_schools",
    "unexperienced",
  ],
  abroad: [
    "exchange",
    "social_projects",
    "work",
    "other",
    "tourism",
    "unexperienced",
  ],
  expertise: [
    "conversation",
    "exam_creation",
    "grammar",
    "other",
    "instrumental_english",
    "dont_have",
  ],
  motivation: [
    "bussiness",
    "personal_growth",
    "flexible_hours",
    "geographical_freedom",
    "love_for_teaching",
    "other",
  ],
  expectation: [
    "financial_growth",
    "own_bussiness",
    "professional_growth",
    "other",
    "flexibility",
  ],
  teaching: [
    "conversation",
    "general_english",
    "grammar",
    "personalized",
    "instrumental",
    "other",
  ],
};
export default {
  name: "PartnerProfile",
  components: {
    PlansDialog,
    PartnerTrainingProgress,
    StudentInvitePanel,
  },
  mixins: [],
  props: {},
  data() {
    return {
      loading: true,
      loadingSave: false,
      savingAvailability: false,
      loadingAddress: false,
      valid: false,
      tab: "tab-1",
      // Card 86akptec0: evolução nos treinamentos de professor.
      trainingProgress: null,
      attrs: {
        class: "mb-6",
        boilerplate: true,
        elevation: 2,
      },
      hasAvatar: false,
      avatar: null,
      profile: {
        name: "",
        phone: "",
        email: "",
        document: "",
        birthDate: "",
        password: "",
        legalGuardian: {},
        address: {
          street: "",
          number: "",
          complement: "",
          neighboorhood: "",
          city: "",
          province: "",
          zipCode: "",
        },
        aviability: [],
      },
      teachingPlans: [],
      planVisibilityBusyId: null,
      deletePlanDialog: false,
      planToDelete: null,
      deletingPlan: false,
      planLinkedStudents: [],
      loadingLinkedStudents: false,
      editPlanWarningDialog: false,
      planToEdit: null,
      rules: {
        validDate: [
          (v) => this.validDate(v) || this.$t("profile.invalidBirth"),
        ],
        validCNPJ: [
          (v) => this.validateCNPJ(v) || this.$t("partnerProfile.invalidCnpj"),
        ],
        document: [
          (v) => this.validateDocNumber(v) || this.$t("checkout.invalidDoc"),
        ],
        email: [
          (v) => !!v || this.$t("common.requiredFieldDot"),
          (v) => /.+@.+\..+/.test(v) || this.$t("common.invalidEmail"),
        ],
        password: [
          (v) => !!v || this.$t("common.requiredFieldDot"),
          (v) =>
            /^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%*#¢+§])[A-Za-z\d@$!%*#?$§-]{8,}$/.test(
              v
            ) || this.$t("profile.passwordRule"),
        ],
        required: [(v) => !!v || this.$t("common.requiredFieldDot")],
      },
      weekDaysItems: WEEK_DAY_ITEMS,
      typeOptions: [
        { text: "Individual", value: "individual" },
        { text: "Dupla", value: "duo" },
        { text: "Trio", value: "trio" },
        { text: "Quarteto", value: "squad" },
      ],

      academicFormationOptions: [
        { text: "Ensino fundamental", value: 1 },
        { text: "Mestrado", value: 4 },
        { text: "Ensino médio", value: 2 },
        { text: "Doutorado", value: 5 },
        { text: "Ensino superior", value: 3 },
      ],

      adaptationOptions: [
        { text: "Totalmente de acordo", value: "AGREE" },
        { text: "Não consigo opinar agora", value: "NEUTRAL" },
        { text: "Parcialmente de acordo", value: "PARTIALY_AGREE" },
        { text: "Discordo", value: "DISAGREE" },
      ],
      professionalInfo: {
        yearsOfExperience: null,
        institutions: [],
        teachingExperiencieAbroad: [],
        expeciality: [],
        academicFormation: null,
        hasCertificate: false,
        certificateType: [],
        diferential: "",
        teachingPeference: [],
        motivation: [],
        expectation: [],
      },
      hoursItemsHalf: [
        "06:00",
        "06:30",
        "07:00",
        "07:30",
        "08:00",
        "08:30",
        "09:00",
        "09:30",
        "10:00",
        "10:30",
        "11:00",
        "11:30",
        "12:00",
        "12:30",
        "13:00",
        "13:30",
        "14:00",
        "14:30",
        "15:00",
        "15:30",
        "16:00",
        "16:30",
        "17:00",
        "17:30",
        "18:00",
        "18:30",
        "19:00",
        "19:30",
        "20:00",
        "20:30",
        "21:00",
        "21:30",
        "22:00",
      ],
    };
  },
  computed: {
    experiencesLevelsOptions() {
      return this.optionsFor("years");
    },
    schoolsTypesOptions() {
      return this.optionsFor("schools");
    },
    teachingExperiencieAbroadOptions() {
      return this.optionsFor("abroad");
    },
    expecialityOptions() {
      return this.optionsFor("expertise");
    },
    expectationOptions() {
      return this.optionsFor("motivation");
    },
    personalExpectationOptions() {
      return this.optionsFor("expectation");
    },
    techingPreferenceOptions() {
      return this.optionsFor("teaching");
    },
    // Os nomes dos exames são internacionais; só "Não possuo" traduz.
    certificatesOptions() {
      return [
        { text: "TOEFL", value: "TOEFL" },
        { text: "TOEIC", value: "TOEIC" },
        { text: "IELTS", value: "IELTS" },
        { text: "PTE", value: "PTE" },
        { text: "Cambridge", value: "CAMBRIDGE" },
        { text: "MET", value: "MET" },
        {
          text: this.$t("partnerProfile.opt.cert.dont_have"),
          value: "dont_have",
        },
      ];
    },
    parentGrades() {
      return [1, 2, 3, 4, 5, 6].map((value) => ({
        text: this.$t(`profile.kin.${value}`),
        value,
      }));
    },
    initials() {
      return this.getInitials(this.profile.fullName);
    },
    hasTrainings() {
      return !!(
        this.trainingProgress &&
        this.trainingProgress.trainings &&
        this.trainingProgress.trainings.length
      );
    },
    visibleTabs() {
      const tabs = [
        {
          value: "tab-1",
          label: this.$t("profile.tabGeneral"),
          icon: "mdi-account-outline",
        },
        {
          value: "tab-2",
          label: this.$t("profile.tabAvailability"),
          icon: "mdi-calendar-clock-outline",
        },
        {
          value: "tab-3",
          label: this.$t("partnerProfile.tabPlan"),
          icon: "mdi-cash-multiple",
        },
        // Card 86akng14v: convite de cadastro do aluno pelo professor.
        {
          value: "tab-invite",
          label: this.$t("partnerProfile.tabInvite"),
          icon: "mdi-account-plus-outline",
        },
      ];
      if (this.profile.pendingInfo) {
        tabs.push({
          value: "tab-4",
          label: this.$t("partnerProfile.tabExperience"),
          icon: "mdi-briefcase-outline",
          alert: true,
        });
      }
      if (this.hasTrainings) {
        tabs.push({
          value: "tab-5",
          label: this.$t("partnerProfile.tabTrainings"),
          icon: "mdi-school-outline",
        });
      }
      return tabs;
    },
  },

  async mounted() {
    this.loadData();
  },
  created() {},
  methods: {
    optionsFor(group) {
      return OPTION_VALUES[group].map((value) => ({
        value,
        text: this.$t(`partnerProfile.opt.${group}.${value}`),
      }));
    },
    formatCurrency(value) {
      return formatBRL(value);
    },
    onFilePick(event) {
      const file = event?.target?.files?.[0];
      if (file) this.avatar = file;
    },
    getInitials(name) {
      const parts = String(name || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean);

      if (parts.length === 0) return "";
      if (parts.length === 1) return parts[0].substring(0, 1).toUpperCase();

      return `${parts[0].substring(0, 1)} ${parts[1]
        .substring(0, 1)
        .toUpperCase()}`;
    },
    isValidAvatarUrl(value) {
      if (!value || typeof value !== "string") return false;

      try {
        new URL(value);
        return true;
      } catch {
        return false;
      }
    },
    // Quadros "Alunos" e "Turmas" (card 17trt6jby7u): a API manda a mesma
    // contagem da listagem do administrativo. Ate carregar, mostra "—".
    countLabel(value) {
      return value === null || value === undefined ? "—" : Number(value);
    },
    async loadData() {
      await Promise.all([
        partnerApi.showProfile().then(async (res) => {
          this.profile = Object.assign({}, res.data);
          this.hasAvatar = this.isValidAvatarUrl(res.data.avatar);
          this.$store.dispatch("setAvatar", res.data.avatar || null);
          this.$store.dispatch("setUser", {
            ...(this.$store.getters.user || {}),
            name: res.data.fullName || this.profile.fullName || "",
            full_name: res.data.fullName || this.profile.fullName || "",
            avatar: res.data.avatar || null,
          });
          this.loading = false;
        }),
        partnerApi.getPartnerPlans().then((res) => {
          this.teachingPlans = res.data.data;
        }),
        // Complementar: se falhar, a aba de treinamentos só não aparece.
        partnerApi
          .getTrainingProgress()
          .then((res) => {
            this.trainingProgress = res.data;
          })
          .catch(() => {
            this.trainingProgress = null;
          }),
      ]);
    },
    validateDocNumber(doc) {
      return cpf.isValid(doc);
    },
    validDate(date) {
      const inputedDate = parse(date, "dd/MM/yyyy", new Date());
      return isValid(inputedDate) && isBefore(inputedDate, new Date());
    },
    validateCNPJ(doc) {
      if (!doc) return true;
      return isValidCnpj(doc);
    },
    toggleAcceptingStudents(value) {
      const previous = this.profile.acceptingNewStudents !== false;
      this.$set(this.profile, "acceptingNewStudents", !!value);
      this.savingAvailability = true;

      partnerApi
        .updateAcceptingStudents(!!value)
        .then(() => {
          this.$root.$emit("display-alert", {
            text: value
              ? this.$t("partnerProfile.visibleAgain")
              : this.$t("partnerProfile.hidden"),
            type: 0,
          });
        })
        .catch(() => {
          this.$set(this.profile, "acceptingNewStudents", previous);
          this.$root.$emit("display-alert", {
            text: this.$t("partnerProfile.availabilityFailed"),
            type: 1,
          });
        })
        .finally(() => {
          this.savingAvailability = false;
        });
    },
    returnToHome() {
      this.$router.push({
        path: "cockpit",
      });
    },
    // Rotulos dos campos invalidos do formulario (so os montados: os da aba
    // de experiencia, por exemplo, nao existem ate a aba ser aberta).
    invalidFieldLabels() {
      const inputs = (this.$refs.form && this.$refs.form.inputs) || [];
      return inputs
        .filter((input) => input.hasError && input.label)
        .map((input) => input.label);
    },
    // Experiencia so vai quando a professora mexeu nela; se mexeu, o tempo de
    // experiencia e obrigatorio (a API recusa sem ele).
    experienceTouched() {
      const info = this.professionalInfo;
      return (
        !!info.yearsOfExperience ||
        [
          info.institutions,
          info.teachingExperiencieAbroad,
          info.expeciality,
          info.certificateType,
          info.teachingPeference,
          info.motivation,
          info.expectation,
        ].some((list) => list.length > 0)
      );
    },
    // A API devolve ou a frase pronta (AppError) ou a lista do ValidationPipe
    // ("address.zipCode must be a string"); da lista tira o nome do campo.
    saveErrorText(err) {
      const status = err && err.response && err.response.status;
      const message = err && err.response && err.response.data?.message;
      if (typeof message === "string" && status && status < 500) {
        return message;
      }
      if (Array.isArray(message) && message.length) {
        const labels = message
          .map((item) => String(item).split(" ")[0].split(".").pop())
          .map((key) => this.fieldLabelFor(key))
          .filter((label, index, all) => label && all.indexOf(label) === index);
        if (labels.length) {
          return this.$t("partnerProfile.invalidFields", {
            fields: labels.join(", "),
          });
        }
        return this.$t("partnerProfile.validationFailed");
      }
      return this.$t("partnerProfile.saveFailed");
    },
    fieldLabelFor(key) {
      const labels = {
        fullName: this.$t("profile.name"),
        phone: this.$t("profile.phone"),
        email: this.$t("profile.email"),
        document: this.$t("profile.cpf"),
        birthDate: this.$t("profile.birthDate"),
        bio: this.$t("partnerProfile.bioLabel"),
        zipCode: this.$t("profile.zip"),
        street: this.$t("profile.street"),
        number: this.$t("profile.number"),
        complement: this.$t("profile.complement"),
        neighboorhood: this.$t("profile.neighborhood"),
        city: this.$t("profile.city"),
        province: this.$t("profile.state"),
        yearsOfExperience: this.$t("partnerProfile.yearsLabel"),
      };
      return labels[key] || key;
    },
    async submit() {
      if (!this.$refs.form.validate()) {
        const labels = this.invalidFieldLabels();
        this.$root.$emit("display-alert", {
          text: labels.length
            ? this.$t("partnerProfile.invalidFields", {
                fields: labels.join(", "),
              })
            : this.$t("profile.fillRequired"),
          type: 1,
        });
        return;
      }

      const info = this.professionalInfo;
      const sendExperience =
        !!this.profile.pendingInfo && this.experienceTouched();
      if (sendExperience && !info.yearsOfExperience) {
        this.tab = "tab-4";
        this.$root.$emit("display-alert", {
          text: this.$t("partnerProfile.yearsRequired"),
          type: 1,
        });
        return;
      }

      this.loadingSave = true;
      if (this.avatar) {
        const formData = new FormData();
        formData.append("avatar", this.avatar);
        await partnerApi.uploadFiles(formData);
      }

      const payload = {
        ...this.profile,
      };

      delete payload.avatar;

      if (payload.cnpj) {
        payload.cnpj = cleanCnpj(payload.cnpj);
      }

      if (sendExperience) {
        if (info.certificateType.length > 0) info.hasCertificate = true;

        payload.aditionalInfo = info;
      }
      partnerApi
        .updateProfile(payload)
        .then(async () => {
          this.$root.$emit("display-alert", {
            text: this.$t("profile.updated"),
            type: 0,
          });
          await this.loadData();
        })
        .catch((err) => {
          this.$root.$emit("display-alert", {
            text: this.saveErrorText(err),
            type: 1,
          });
        })
        .finally(() => {
          this.loadingSave = false;
        });
    },
    getAddressByZipCode() {
      if (this.profile.address.zipCode) {
        addressAPI
          .getAddresByZipCode(this.profile.address.zipCode)
          .then(async (res) => {
            this.$refs.streetInput.handleInput(res.data.address);
            this.$refs.neighboorhoodInput.handleInput(res.data.district);
            this.$refs.cityInput.handleInput(res.data.city);
            this.$refs.provinceInput.handleInput(res.data.state);
            this.readOnly = false;
            this.loadingAddress = false;
          })
          .catch(() => {
            this.loadingAddress = false;
            this.readOnly = false;
          });
      }
    },
    openPlanForm() {
      this.$refs.planForm.openDialog();
    },
    editPlan(plan) {
      // Se o plano tiver alunos vinculados, avisa que as proximas mensalidades
      // deles mudam antes de abrir o formulario (card 86ahpuh5h).
      this.planToEdit = plan;
      this.planLinkedStudents = [];
      partnerApi
        .getPlanLinkedStudents(plan.id)
        .then((res) => {
          const linked = res.data || [];
          if (linked.length) {
            this.planLinkedStudents = linked;
            this.editPlanWarningDialog = true;
          } else {
            this.$refs.planForm.openDialog(plan);
          }
        })
        .catch(() => {
          // Falha ao checar vinculos nao deve travar a edicao.
          this.$refs.planForm.openDialog(plan);
        });
    },
    proceedEditPlan() {
      this.editPlanWarningDialog = false;
      if (this.planToEdit) {
        this.$refs.planForm.openDialog(this.planToEdit);
      }
    },
    savePlan(data) {
      const request = data.id
        ? partnerApi.updatePartnerPlan(data.id, data)
        : partnerApi.createPartnerPlan(data);
      request
        .then((res) => {
          this.$refs.planForm.closeDialog();
          if (data.id) {
            const idx = this.teachingPlans.findIndex((p) => p.id === data.id);
            if (idx !== -1) this.$set(this.teachingPlans, idx, res.data);
          } else {
            this.teachingPlans.push(res.data);
          }
          this.$root.$emit("display-alert", {
            text: data.id
              ? this.$t("partnerProfile.planUpdated")
              : this.$t("partnerProfile.planAdded"),
            type: 0,
          });
        })
        .catch((err) => {
          const message = Array.isArray(err?.response?.data?.message)
            ? err.response.data.message[0]
            : err?.response?.data?.message ||
              this.$t("partnerProfile.planSaveFailed");
          this.$root.$emit("display-alert", { text: message, type: 1 });
        });
    },
    // Retorno de 30/10: o plano segue valendo para quem já está nele; o
    // switch só decide se aparece na página de match para alunos novos.
    togglePlanVisibility(plan, value) {
      const previous = plan.showToNewStudents !== false;
      this.$set(plan, "showToNewStudents", !!value);
      this.planVisibilityBusyId = plan.id;

      return partnerApi
        .setPlanVisibility(plan.id, !!value)
        .catch((err) => {
          this.$set(plan, "showToNewStudents", previous);
          this.$root.$emit("display-alert", {
            text:
              err?.response?.data?.message ||
              this.$t("partnerProfile.planSaveFailed"),
            type: 1,
          });
        })
        .finally(() => {
          this.planVisibilityBusyId = null;
        });
    },
    confirmDeletePlan(plan) {
      this.planToDelete = plan;
      this.planLinkedStudents = [];
      this.deletePlanDialog = true;
      this.loadingLinkedStudents = true;
      partnerApi
        .getPlanLinkedStudents(plan.id)
        .then((res) => {
          this.planLinkedStudents = res.data || [];
        })
        .catch(() => {
          this.planLinkedStudents = [];
        })
        .finally(() => {
          this.loadingLinkedStudents = false;
        });
    },
    deletePlan() {
      if (!this.planToDelete) return;
      this.deletingPlan = true;
      partnerApi
        .deletePartnerPlan(this.planToDelete.id)
        .then(() => {
          const idx = this.teachingPlans.findIndex(
            (p) => p.id === this.planToDelete.id
          );
          if (idx !== -1) this.teachingPlans.splice(idx, 1);
          this.deletePlanDialog = false;
          this.planToDelete = null;
          this.$root.$emit("display-alert", {
            text: this.$t("partnerProfile.planRemoved"),
            type: 0,
          });
        })
        .catch((err) => {
          const message = Array.isArray(err?.response?.data?.message)
            ? err.response.data.message[0]
            : err?.response?.data?.message ||
              this.$t("partnerProfile.planRemoveFailed");
          this.deletePlanDialog = false;
          this.$root.$emit("display-alert", { text: message, type: 1 });
        })
        .finally(() => {
          this.deletingPlan = false;
        });
    },
    displayFormat(format) {
      // Mantém o comportamento antigo: formato fora de 1–4 fica em branco.
      return [1, 2, 3, 4].includes(format) ? displayGroupFormat(format) : "";
    },
    displayFrequency(frequency) {
      return Number(frequency) === 5
        ? this.$t("planForm.freq.5")
        : displayPlanFrequency(frequency);
    },
    triggerFileInput() {
      document.getElementById("fileInput").click();
    },
    displayCertificateName(name) {
      return name === "dont_have" ? this.$t("partnerProfile.noneCert") : name;
    },
    updateEnd(av) {
      if (!av.from) return;

      const duration = this.profile.perClass || 60;
      const [h, m] = av.from.split(":").map(Number);
      const d = new Date(2000, 0, 1, h, m);
      d.setMinutes(d.getMinutes() + duration);

      const hh = String(d.getHours()).padStart(2, "0");
      const mm = String(d.getMinutes()).padStart(2, "0");

      const result = `${hh}:${mm}`;
      // só sobrescreve quando o horário calculado existe na lista; caso
      // contrário preserva o valor que o usuário tenha escolhido manualmente.
      if (this.hoursItemsHalf.includes(result)) {
        av.to = result;
      }
    },
    async removerDisponibilidade(id, index) {
      if (id) {
        await partnerApi
          .removerDisponibilidade(id)
          .then(() => {
            this.$root.$emit("display-alert", {
              text: this.$t("profile.availabilityUpdated"),
              type: 0,
            });
            this.loadData();
          })
          .catch(() => {
            this.$root.$emit("display-alert", {
              text: this.$t("partnerProfile.availabilityError"),
              type: 1,
            });
          });
      } else {
        this.profile.aviability.splice(index, 1);
      }
    },
    async inserirHorario() {
      if (!Array.isArray(this.profile.aviability)) {
        this.$set(this.profile, "aviability", []);
      }
      this.profile.aviability.push({
        weekDay: null,
        from: null,
        to: null,
        editable: true,
      });
    },
  },
  watch: {
    "profile.birthDate": {
      handler: function (value) {
        if (value) {
          try {
            const birthDate = parse(value, "dd/MM/yyyy", new Date());

            const difference = differenceInYears(new Date(), birthDate);
            if (difference < 18) {
              this.profile.underAge = true;
            } else {
              this.profile.underAge = false;
            }
          } catch (e) {
            return e;
          }
        }
      },
      deep: true,
    },
    avatar: function (value) {
      if (value) {
        this.profile.avatar = URL.createObjectURL(value);
        this.hasAvatar = true;
      }
    },
  },
};
</script>
<style scoped>
.pf {
  --ink: var(--dk-ink, #231a2e);
  --muted: var(--dk-muted, #6b6472);
  --paper: var(--dk-paper, #fbf7fa);
  --line: rgba(var(--dk-ink-rgb, 35, 26, 46), 0.09);
  --accent: var(--dk-grape, #a826e1);
  --accent-soft: rgba(168, 38, 225, 0.1);
  --teal: var(--dk-teal, #00a379);
  --ember: var(--dk-ember, #ff5d2a);
  width: 100%;
  padding: 8px 4px 48px;
  color: var(--ink);
}

.pf-display {
  font-family: "Poppins", sans-serif;
  letter-spacing: -0.01em;
}

.pf-file {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

.pf-eyebrow {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--accent);
}
.pf-eyebrow--ember {
  color: var(--ember);
}

/* ---------- Carteira ---------- */
.pf-card-id {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 24px;
  align-items: center;
  margin-top: 12px;
  padding: 24px 28px;
  border-radius: 24px;
  background: radial-gradient(
      120% 150% at 0% 0%,
      var(--accent-soft),
      transparent 55%
    ),
    var(--dk-surface, #fff);
  border: 1px solid var(--line);
  box-shadow: 0 22px 48px -38px rgba(var(--dk-shadow-rgb, 35, 26, 46), 0.55);
}
.pf-avatar {
  position: relative;
  width: 116px;
  height: 116px;
  border-radius: 50%;
  padding: 0;
  cursor: pointer;
  background: var(--accent);
  border: 3px solid var(--dk-surface, #fff);
  box-shadow: 0 0 0 3px var(--accent-soft),
    0 12px 26px -12px rgba(168, 38, 225, 0.6);
  overflow: hidden;
}
.pf-avatar__img {
  width: 100%;
  height: 100%;
}
.pf-avatar__initials {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #fff;
  font-size: 2.4rem;
  font-weight: 800;
  text-transform: uppercase;
}
.pf-avatar__cam {
  position: absolute;
  inset: auto 0 0 0;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(var(--dk-ink-rgb, 35, 26, 46), 0.55);
  opacity: 0;
  transition: opacity 0.2s ease;
}
.pf-avatar:hover .pf-avatar__cam,
.pf-avatar:focus-visible .pf-avatar__cam {
  opacity: 1;
}
.pf-id__name {
  margin: 4px 0 0;
  font-size: clamp(1.7rem, 3.4vw, 2.35rem);
  font-weight: 700;
  line-height: 1.05;
  color: var(--ink);
}
.pf-id__tiles {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 20px;
  margin-top: 14px;
}
.pf-tile {
  display: flex;
  flex-direction: column;
  line-height: 1.05;
}
.pf-tile__num {
  font-size: 1.9rem;
  font-weight: 700;
  color: var(--accent);
}
.pf-tile__label {
  font-size: 0.74rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--muted);
}
.pf-id__quals {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
}
.pf-chip {
  display: inline-flex;
  align-items: center;
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--ink);
  background: var(--dk-surface, #fff);
  border: 1px solid var(--line);
}
.pf-chip .v-icon {
  color: var(--accent);
}
.pf-chip--tutor {
  color: #fff;
  background: linear-gradient(
    135deg,
    var(--dk-grape, #a826e1),
    var(--dk-grape-deep, #7d1baa)
  );
  border-color: transparent;
}
.pf-chip--tutor .v-icon {
  color: #fff;
}
.pf-id__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-end;
}

/* Botões */
.pf-btn.v-btn {
  background: var(--accent) !important;
  color: #fff !important;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 700;
}
.pf-btn-ghost.v-btn {
  color: var(--accent) !important;
  border-color: var(--accent) !important;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 700;
}

/* ---------- Rail de abas ---------- */
.pf-avail {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 16px;
  padding: 14px 20px;
  background: var(--dk-surface, #fff);
  border: 1px solid var(--line);
  border-radius: 16px;
  box-shadow: 0 12px 30px -28px rgba(var(--dk-shadow-rgb, 35, 26, 46), 0.5);
}
.pf-avail__switch {
  flex: 0 0 auto;
  margin: 0;
  padding: 0;
}
.pf-avail__title {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--ink);
}
.pf-avail__sub {
  margin-top: 2px;
  font-size: 0.82rem;
  color: var(--muted);
}

.pf-rail {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 22px 0 16px;
  padding: 6px;
  background: var(--dk-surface, #fff);
  border: 1px solid var(--line);
  border-radius: 16px;
  box-shadow: 0 12px 30px -28px rgba(var(--dk-shadow-rgb, 35, 26, 46), 0.5);
}
.pf-rail__tab {
  display: inline-flex;
  align-items: center;
  padding: 9px 16px;
  border-radius: 11px;
  font-size: 0.86rem;
  font-weight: 600;
  color: var(--muted);
  background: transparent;
  transition: background 0.18s ease, color 0.18s ease;
}
.pf-rail__tab .v-icon {
  color: currentColor;
}
.pf-rail__tab:hover {
  color: var(--ink);
  background: var(--paper);
}
.pf-rail__tab--on {
  color: #fff;
  background: var(--accent);
  box-shadow: 0 10px 20px -12px rgba(168, 38, 225, 0.7);
}
.pf-rail__tab--on:hover {
  color: #fff;
  background: var(--accent);
}
.pf-rail__tab--alert {
  color: var(--ember);
}
.pf-rail__tab--alert.pf-rail__tab--on {
  color: #fff;
  background: var(--ember);
  box-shadow: 0 10px 20px -12px rgba(255, 93, 42, 0.7);
}

.pf-tabs-items {
  background: transparent !important;
  overflow: visible;
}

/* ---------- Cards / seções ---------- */
.pf-card {
  background: var(--dk-surface, #fff);
  border: 1px solid var(--line);
  border-radius: 20px;
  box-shadow: 0 18px 40px -34px rgba(var(--dk-shadow-rgb, 35, 26, 46), 0.5);
}
.pf-card--pad {
  padding: 24px 26px;
}
.pf-section-title {
  font-size: 1.02rem;
  font-weight: 700;
  color: var(--ink);
}
.pf-section-sub {
  margin-top: 4px;
  font-size: 0.85rem;
  color: var(--muted);
  line-height: 1.45;
}
.pf-strong {
  font-weight: 700;
  color: var(--ink);
}
.pf-field-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--muted);
  margin-bottom: 6px;
}
.pf-subhead {
  margin: 18px 0 10px;
}
.pf-inset {
  padding: 18px;
  border: 1px solid var(--line);
  border-radius: 14px;
  background: var(--paper);
}
.pf-empty {
  margin-top: 20px;
  padding: 28px;
  text-align: center;
  font-size: 0.88rem;
  color: var(--muted);
  border: 1px dashed var(--line);
  border-radius: 14px;
  background: var(--paper);
}

/* ---------- Disponibilidade ---------- */
.pf-slot {
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr auto;
  gap: 14px;
  align-items: end;
  margin-top: 16px;
  padding: 14px 16px;
  border-radius: 14px;
  background: var(--paper);
  border: 1px solid var(--line);
}
.pf-slot__del.v-btn {
  color: var(--ember) !important;
  margin-bottom: 2px;
}
.pf-linked-list {
  margin: 8px 0 12px;
  padding-left: 20px;
  max-height: 180px;
  overflow-y: auto;
}
.pf-linked-list li {
  font-weight: 600;
  line-height: 1.7;
}
.pf-plan-warn {
  color: var(--muted, var(--dk-muted, #6b6472));
  font-size: 0.85rem;
}
.pf-add {
  display: inline-flex;
  align-items: center;
  margin-top: 18px;
  padding: 10px 18px;
  border-radius: 999px;
  font-size: 0.86rem;
  font-weight: 700;
  color: var(--accent);
  background: var(--accent-soft);
  transition: background 0.18s ease;
}
.pf-add:hover {
  background: rgba(168, 38, 225, 0.18);
}
.pf-add .v-icon {
  color: var(--accent);
}

/* ---------- Plano de ensino (tabela) ---------- */
.pf-plans-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.pf-table {
  margin-top: 18px;
}
.pf-table__head,
.pf-table__row {
  display: grid;
  grid-template-columns: 1.2fr 1.2fr 1fr 1fr 1.3fr 0.8fr;
  gap: 12px;
  align-items: center;
}
.pf-table__head {
  padding: 0 14px 10px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--muted);
  border-bottom: 1px solid var(--line);
}
.pf-table__acts-h {
  text-align: right;
}
.pf-table__row {
  padding: 14px;
  font-size: 0.9rem;
  border-radius: 12px;
  transition: background 0.15s ease;
}
.pf-table__row:hover {
  background: var(--paper);
}
.pf-plans-hint {
  max-width: 560px;
}
.pf-match {
  display: flex;
  align-items: center;
  gap: 8px;
}
.pf-match__switch {
  margin: 0;
  padding: 0;
  flex: 0 0 auto;
}
.pf-match__label {
  font-size: 0.8rem;
  color: var(--muted);
}
.pf-money {
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}
.pf-money--in {
  color: var(--teal);
}
.pf-money--off {
  color: var(--muted);
}
.pf-table__acts {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

/* ---------- Experiência (perguntas) ---------- */
.pf-q {
  padding: 20px 0;
  border-bottom: 1px solid var(--line);
}
.pf-q--last {
  border-bottom: none;
  padding-bottom: 4px;
}
.pf-q__label {
  display: block;
  margin-bottom: 8px;
  font-size: 0.95rem;
  color: var(--ink);
}

/* ---------- Responsivo ---------- */
@media (max-width: 900px) {
  .pf-card-id {
    grid-template-columns: auto 1fr;
  }
  .pf-id__actions {
    grid-column: 1 / -1;
    justify-content: flex-start;
  }
}
@media (max-width: 640px) {
  .pf-card-id {
    grid-template-columns: 1fr;
    justify-items: start;
    text-align: left;
  }
  .pf-slot {
    grid-template-columns: 1fr 1fr;
  }
  .pf-table__head {
    display: none;
  }
  .pf-table__row {
    grid-template-columns: 1fr 1fr;
    background: var(--paper);
    margin-bottom: 8px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .pf-avatar__cam,
  .pf-rail__tab,
  .pf-add,
  .pf-table__row {
    transition: none;
  }
}
</style>
